import {Input} from 'antd';
import {format} from 'date-fns';
import {inject, observer} from 'mobx-react';
import {rgba} from 'polished';
import * as React from 'react';
import Helmet from 'react-helmet';
import {Link} from 'react-router-dom';
import {BaseButton} from '../../components/Button';
import Card, {CardBody, CardFooter, CardHeader} from '../../components/Card';
import Flex from '../../components/Flex';
import Icon from '../../components/Icon';
import Layout, {Main} from '../../components/Layout';
import MoneyFormat from '../../components/MoneyFormat';
import Persona from '../../components/Persona';
import ReservationStatusBadge from '../../components/ReservationStatusBadge';
import Text from '../../components/Text';
import {ListingProviderType} from '../../models/enums';
import {ReservationsListType} from '../../models/ReservationsListModel';
import {styled} from '../../utils';

interface WithInjectedProps {
  reservationsList: ReservationsListType;
}

const ReservationsGrid = styled('div')({
  margin: -25
});

const ReservationsGridItem = styled(Flex)(({theme}) => ({
  boxSizing: 'border-box',
  padding: '20px 0px',
  borderLeft: `2px solid transparent`,
  borderBottom: '1px solid #F1F3F6',
  '&:nth-child(even)': {
    background: rgba('#F1F3F6', 0.3)
  },
  '&:hover': {
    background: rgba('#F1F3F6', 0.3),
    borderLeftColor: theme.color.secondary
  }
}));

const ReservationListingInfo = styled(Flex)({
  boxSizing: 'border-box',
  borderRight: '1px solid #F1F3F6',
  padding: '0 20px',
  width: 352
});

const ReservationListingImage = styled('img')({
  width: 87,
  borderRadius: 3
});

const GuestInfo = styled(Flex)({
  boxSizing: 'border-box',
  padding: '0 20px',
  width: 240,
  height: 72,
  borderRight: '1px solid #F1F3F6',
  justifyContent: 'space-between'
});

const ReservationBasicInfo = styled(Flex)({
  boxSizing: 'border-box',
  padding: '0 20px',
  width: 240,
  height: 72,
  borderRight: '1px solid #F1F3F6',
  justifyContent: 'space-between'
});

const ReservationDate = styled('span')({
  color: '#7E8DA6'
});

@inject('reservationsList')
@observer
export default class Reservations extends React.Component<{}, {}> {
  public componentDidMount() {
    this._withInjectedProps.reservationsList.loadEntries();
  }

  public render() {
    const {
      pagedList,
      totalCount,
      isLoaded,
      isLoading,
      loadPreviousPage,
      loadNextPage,
      pagination: {hasNext, hasPrevious, hasCached}
    } = this._withInjectedProps.reservationsList;

    return (
      <React.Fragment>
        <Helmet>
          <title>Reservations list</title>
        </Helmet>
        <Layout>
          <Main>
            <Card>
              <CardBody>
                <Flex justify="flex-end" style={{marginBottom: 16}}>
                  {/* <Select
                    style={{flexBasis: 120}}
                    onSelect={value =>
                      this._withInjectedProps.reservationsList.setSearchByField(
                        value
                      )
                    }
                    defaultValue="name"
                  >
                    <Select.Option title="By code" value="confirmationCode">
                      By confirmation code
                    </Select.Option>
                    <Select.Option title="By team ID" value="teamId">
                      By team ID
                    </Select.Option>
                    <Select.Option title="By guest name" value="guestName">
                      By guest name
                    </Select.Option>
                  </Select> */}
                  <Input.Search
                    placeholder="Team ID"
                    onSearch={value => {
                      if (value) {
                        this._withInjectedProps.reservationsList.setFilterByTeamId(
                          parseInt(value, 10)
                        );
                      }
                    }}
                    allowClear={true}
                    type="number"
                    style={{width: 300}}
                  />
                  <Input.Search
                    placeholder="Search query"
                    onSearch={value => this._handleSearch(value)}
                    allowClear={true}
                    style={{width: 300}}
                  />
                </Flex>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <Flex justify="space-between">
                  <span>
                    Reservations {isLoaded ? `(${totalCount})` : null}
                  </span>
                  <div>
                    {hasPrevious ? (
                      <BaseButton
                        disabled={isLoading}
                        onClick={loadPreviousPage}
                      >
                        <Icon name="left" />
                      </BaseButton>
                    ) : null}
                    {hasNext ? (
                      <BaseButton disabled={isLoading} onClick={loadNextPage}>
                        <Icon name="right" />
                      </BaseButton>
                    ) : null}
                  </div>
                </Flex>
              </CardHeader>
              
              <CardBody>
                <ReservationsGrid>
                  {isLoading && !hasCached ? (
                    <Flex
                      center={true}
                      direction="column"
                      style={{height: 150}}
                    >
                      <Icon
                        spin={true}
                        name="loading"
                        size={24}
                        color="#7C4EF6"
                      />
                    </Flex>
                  ) : (
                    pagedList.map((entry, index) => {
                      return (
                        <Link
                          key={index}
                          to={`/messages/external/${entry.channelId}`}
                          style={{color: 'inherit', textDecoration: 'none'}}
                        >
                          <ReservationsGridItem>
                            <ReservationListingInfo>
                              <div style={{marginRight: 12}}>
                                <ReservationListingImage
                                  src={entry.listing.previewImageUrl}
                                />
                              </div>

                              <div>
                                <Text
                                  withComponent="h5"
                                  fontSize={16}
                                  lineHeight="22px"
                                >
                                  {entry.listing.name}
                                </Text>
                                {entry.listing.internalName ? (
                                  <Text
                                    color="secondary"
                                    fontSize={12}
                                    style={{marginTop: 8, marginBottom: 8}}
                                    withComponent="div"
                                  >
                                    {entry.listing.internalName}
                                  </Text>
                                ) : null}
                                <ReservationStatusBadge status={entry.state} />
                              </div>
                            </ReservationListingInfo>

                            <GuestInfo direction="column">
                              <div>
                                <Text fontSize={16} style={{marginRight: 5}}>
                                  <Persona
                                    badge={entry.source as ListingProviderType}
                                  >
                                    {entry.guest.name}
                                  </Persona>
                                </Text>
                              </div>

                              <Flex
                                justify="space-between"
                                style={{width: 150}}
                              >
                                <Text>Check-in</Text>
                                <ReservationDate>
                                  {format(entry.checkInDate, 'DD/MM/YYYY')}
                                </ReservationDate>
                              </Flex>

                              <Flex
                                justify="space-between"
                                style={{width: 150}}
                              >
                                <Text>Check-out</Text>
                                <ReservationDate>
                                  {format(entry.checkOutDate, 'DD/MM/YYYY')}
                                </ReservationDate>
                              </Flex>
                            </GuestInfo>

                            <ReservationBasicInfo direction="column">
                              <div>
                                <Text fontSize={16}>Reservation</Text>
                              </div>

                              <Flex
                                justify="space-between"
                                style={{width: 150}}
                              >
                                <Text withComponent="div">Code</Text>

                                <ReservationDate>
                                  {entry.confirmationCode}
                                </ReservationDate>
                              </Flex>
                              <Flex
                                justify="space-between"
                                style={{width: 150}}
                              >
                                <Text withComponent="div">Created at</Text>
                                <ReservationDate>
                                  {format(
                                    entry.createdAt * 1000,
                                    'DD/MM/YYYY'
                                  )}
                                </ReservationDate>
                              </Flex>
                            </ReservationBasicInfo>
                            <div style={{padding: 20}}>
                              <Text fontSize={26} color="#1B202A">
                                {entry.earning && entry.currency ? (
                                  <MoneyFormat
                                    amount={entry.earning}
                                    currency={entry.currency}
                                  />
                                ) : null}
                              </Text>
                            </div>
                          </ReservationsGridItem>
                        </Link>
                      );
                    })
                  )}
                </ReservationsGrid>
              </CardBody>
              {hasNext || hasPrevious ? (
                <CardFooter>
                  <Flex justify="flex-end">
                    {hasPrevious ? (
                      <BaseButton
                        disabled={isLoading}
                        onClick={loadPreviousPage}
                      >
                        <Icon name="left" />
                      </BaseButton>
                    ) : null}
                    {hasNext ? (
                      <BaseButton disabled={isLoading} onClick={loadNextPage}>
                        <Icon name="right" />
                      </BaseButton>
                    ) : null}
                  </Flex>
                </CardFooter>
              ) : null}
            </Card>
          </Main>
        </Layout>
      </React.Fragment>
    );
  }

  private _handleSearch(searchQuery: string) {
    this._withInjectedProps.reservationsList.filter(searchQuery);
  }

  private get _withInjectedProps() {
    return this.props as WithInjectedProps;
  }
}



// WEBPACK FOOTER //
// ./src/pages/Reservations/Reservations.tsx